import { useEffect, useMemo } from 'react'
import queryString from 'query-string'
import { datadogLogs } from '@datadog/browser-logs'

import { mapLocalStorageState } from './localStorageStateMappers/browserStateMapper'
import { Store } from './types'
import { LocalStorage, LocalStorageErrorType } from './utils'
import { getDefaultPersistentState } from '../persistentState/config'

import useQuoteStore from 'src/store/store'
import { Quote } from 'src/store/types'
import { assertNever } from 'utils/assertNever'
import { report } from 'utils/logging/logging'

export const useDirectFlowStore: Store = (quoteId, { navigate, location, track }) => {
  const { utm_campaign } = queryString.parse(location.search)
  const persistentStateLocalStorage = LocalStorage(quoteId)
  const localStorageState = persistentStateLocalStorage.get()
  const { getQuoteData, createQuote } = useQuoteStore()

  const data = useMemo(() => {
    if (!('errorType' in localStorageState)) {
      const newState = mapLocalStorageState({
        localStorageState,
      })
      persistentStateLocalStorage.sync(newState)

      const zustandQuoteData = getQuoteData(quoteId) as Quote

      createQuote(quoteId, {
        ...(newState as Partial<Quote>),
        ...zustandQuoteData,
        pageState: {
          ...newState.pageState,
          ...(zustandQuoteData?.pageState as Quote['pageState']),
        },
      })

      return newState
    } else {
      const persistentState = getDefaultPersistentState()

      const query = queryString.parse(location.search) as {
        referralCode?: string
        irclickid?: string
        utm_source?: string
      }
      if (query.referralCode) {
        persistentState.quoteGeneralInfo.referralCode = query.referralCode
      }
      if (query.irclickid) {
        persistentState.quoteGeneralInfo.impactClickId = query.irclickid
      }
      if (query.utm_source) {
        persistentState.quoteGeneralInfo.utmSource = query.utm_source
      }

      return persistentState
    }
  }, [])

  useEffect(() => {
    if (!('errorType' in localStorageState)) {
      return
    }
    const errorType = localStorageState.errorType
    switch (errorType) {
      case LocalStorageErrorType.NOT_FOUND: {
        // Not finding the state in local storage, should only happen
        // when coming from /start-quote, if not, we redirect back to it
        // to generate a new quote id
        if (!location.state?.fromStartNewQuote) {
          if (utm_campaign?.includes('ABBA_PreQuote')) {
            datadogLogs.logger.warn(
              'Local storage not found - user resumed a pre-quote ABBA campaign',
            )
          } else if (localStorage.getItem(`quote-${quoteId}-completed`)) {
            navigate(`/quote/${quoteId}/continue-quote`, { replace: true })
            break
          } else {
            datadogLogs.logger.warn('LocalStorage error', { errorType })
          }
          navigate('/start-quote', { replace: true })
        }
        break
      }
      case LocalStorageErrorType.INVALID: {
        report(new Error(), 'LocalStorage error', { level: 'warning', args: { errorType } })
        break
      }
      case LocalStorageErrorType.INCOMPATIBLE: {
        report(new Error(), 'LocalStorage error', { level: 'warning', args: { errorType } })
        track('localStorageResetState')
        break
      }
      default: {
        report(new Error(), 'LocalStorage error', { level: 'warning', args: { errorType } })
        assertNever(errorType)
        break
      }
    }
  }, [])

  return {
    data,
    isLoading: false,
    sync: persistentStateLocalStorage.sync,
    clear: persistentStateLocalStorage.clear,
  }
}
